const MODEL = (window?.BOTUI?.OPENAI_MODEL || "gpt-4.1-mini");

function detectLanguage(s){
  if(!s) return "hinglish";
  const hasHindi=/[\u0900-\u097F]/.test(s), hasLatin=/[A-Za-z]/.test(s);
  if (hasHindi && hasLatin) return "hinglish";
  return hasHindi ? "hi" : hasLatin ? "en" : "hinglish";
}

const RECT_SCHEMA = {
  name: "parse_rectangle_command",
  schema: {
    type: "object", additionalProperties: false,
    properties: {
      intent: { type: "string", enum: ["draw_rectangle","delete_rectangle","unknown"] },
      language: { type: "string", enum: ["hi", "en", "hinglish"] },
      slots: {
        type: "object", additionalProperties: false,
        properties: {
          cell: { type: ["string","null"] },
          layer: { type: ["string","null"] },
          size: {
            type: ["object","null"], additionalProperties: false,
            properties: { w:{type:["number","null"]}, h:{type:["number","null"]}, square:{type:["number","null"]}, units:{type:["string","null"], enum:["nm","um","µm","mm",null]} }
          },
          placement: {
            type: ["object","null"], additionalProperties: false,
            properties: {
              mode: { type:["string","null"], enum:["origin","center","two_points",null] },
              point:{ type:["array","null"], items:{type:"number"}, minItems:2, maxItems:2 },
              center:{ type:["array","null"], items:{type:"number"}, minItems:2, maxItems:2 },
              p1:{ type:["array","null"], items:{type:"number"}, minItems:2, maxItems:2 },
              p2:{ type:["array","null"], items:{type:"number"}, minItems:2, maxItems:2 }
            }
          },
          deletion: {
            type:["object","null"], additionalProperties:false,
            properties:{
              selected:{type:["boolean","null"]},
              ids:{type:["array","null"], items:{type:"integer"}},
              region:{type:["object","null"], additionalProperties:false,
                properties:{ p1:{type:"array",items:{type:"number"},minItems:2,maxItems:2},
                            p2:{type:"array",items:{type:"number"},minItems:2,maxItems:2}}
              },
              size:{type:["object","null"], additionalProperties:false,
                properties:{ w:{type:["number","null"]}, h:{type:["number","null"]}, units:{type:["string","null"], enum:["nm","um","µm","mm",null]} }
              },
              cell:{type:["string","null"]}
            }
          },
          rotation: { type: ["number","null"] },
          repeat: {
            type: ["object","null"], additionalProperties:false,
            properties: { nx:{type:["integer","null"]}, ny:{type:["integer","null"]}, dx:{type:["number","null"]}, dy:{type:["number","null"]}, units:{type:["string","null"], enum:["nm","um","µm","mm",null]} }
          }
        }
      }
    },
    required: ["intent","language","slots"]
  },
  strict: true
};

export async function parseWithLLM(userText){
  const apiKey = window?.BOTUI?.OPENAI_API_KEY || window?.ENV?.OPENAI_API_KEY || process?.env?.OPENAI_API_KEY;
  if(!apiKey) throw new Error("OPENAI_API_KEY missing");

  const lang = detectLanguage(userText);

  const sys = [
    "You are a CAD layout assistant. Extract rectangle commands as strict JSON.",
    "User may speak Hindi/English/Hinglish with slang and free order.",
    "Never infer missing values. Use null for missing.",
    "placement rules:",
    "- 'from x1,y1 to x2,y2' -> {mode:'two_points', p1:[x1,y1], p2:[x2,y2]}",
    "- 'center cx,cy' or 'center at cx,cy' -> {mode:'center', center:[cx,cy]}",
    "- Any lone 'x,y' or 'x y' or 'coordinates ...' -> {mode:'origin', point:[x,y]}",
    "size rules: detect 'w x h' or 'w by h' or named width/height; units can be nm/um/µm/mm.",
    "If it's not a rectangle command, intent='unknown'."
  ].join("\n");

  // few-shot to bias model for conversational phrases
  const examples = [
    {role:"user", content:"haan bhai ek rectangle bana M1 layer me 100 by 200 ka 2,6 coordinates par"},
    {role:"assistant", content: JSON.stringify({intent:"draw_rectangle", language:"hinglish", slots:{layer:"M1", size:{w:100,h:200,units:null}, placement:{mode:"origin",point:[2,6]}, rotation:null, repeat:null}})},

    {role:"user", content:"from 0,0 to 10,5 on M1"},
    {role:"assistant", content: JSON.stringify({intent:"draw_rectangle", language:"en", slots:{layer:"M1", size:null, placement:{mode:"two_points",p1:[0,0],p2:[10,5]}, rotation:null, repeat:null}})},

    {role:"user", content:"center 5 5, size 4 by 2, M1"},
    {role:"assistant", content: JSON.stringify({intent:"draw_rectangle", language:"en", slots:{layer:"M1", size:{w:4,h:2,units:null}, placement:{mode:"center",center:[5,5]}, rotation:null, repeat:null}})},
    // delete selected
    {role:"user", content:"delete selected rectangles"},
    {role:"assistant", content: JSON.stringify({intent:"delete_rectangle", language:"en", slots:{deletion:{selected:true}}})},

    // delete by region + layer + cell
    {role:"user", content:"Cell azeem, delete rectangles on M1 from 0,0 to 50,50"},
    {role:"assistant", content: JSON.stringify({intent:"delete_rectangle", language:"en", slots:{layer:"M1", deletion:{cell:"azeem", region:{p1:[0,0], p2:[50,50]}}}})},

    // delete by size
    {role:"user", content:"Remove 5 by 5 rectangles on layer 8:0"},
    {role:"assistant", content: JSON.stringify({intent:"delete_rectangle", language:"en", slots:{layer:"8:0", deletion:{size:{w:5,h:5,units:null}}}})},

  ];

  const payload = {
    model: MODEL,
    input: [{role:"system", content:sys}, ...examples, {role:"user", content:userText}],
    response_format: { type:"json_schema", json_schema: RECT_SCHEMA }
  };

  const res = await fetch("https://api.openai.com/v1/responses", {
    method:"POST",
    headers:{ "Content-Type":"application/json", Authorization:`Bearer ${apiKey}` },
    body: JSON.stringify(payload)
  });
  if(!res.ok){ throw new Error(`OpenAI ${res.status}`); }
  const data = await res.json();
  const raw = data?.output?.[0]?.content?.[0]?.text ?? data?.output_text ?? data?.choices?.[0]?.message?.content;
  const parsed = JSON.parse(raw);
  if(!parsed.language) parsed.language = lang;

  // soft repair: if model forgot a simple pair like "2,6" present in text
  if(!parsed?.slots?.placement){
    const quick = userText.match(/(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/);
    if(quick){
      parsed.slots = parsed.slots || {};
      parsed.slots.placement = { mode:"origin", point:[parseFloat(quick[1]), parseFloat(quick[2])] };
    }
  }
  return parsed;
}
